/**
 * multiday-pricing — prezzi noleggio MULTI-GIORNO per-stagione.
 *
 * Ogni stagione (seasonal_prices) può avere `multiday_tiers`: fasce di durata
 * configurabili con PREZZO TOTALE, season-dependent by construction:
 *   [{ min_days, max_days, price_total }]   (2..6 gg; 7+ = settimanale)
 *
 * Il resolver span: per un noleggio di N giorni, risolve la stagione (del
 * check-in) → se un tier contiene N → usa `price_total` come totale del noleggio,
 * SCAVALCANDO il calcolo per-giorno (×N). Se nessun tier → resta il per-giorno.
 * Il markup cliente si applica sul totale a valle (come le altre voci).
 */

export interface MultidayTier {
  min_days: number;
  max_days: number;
  price_total: number;
}

/** Parse difensivo dei tier da JSON (colonna jsonb, può essere sporca). */
export function parseMultidayTiers(raw: unknown): MultidayTier[] {
  if (!Array.isArray(raw)) return [];
  const out: MultidayTier[] = [];
  for (const r of raw) {
    if (!r || typeof r !== 'object') continue;
    const o = r as Record<string, unknown>;
    const min = Number(o.min_days);
    const max = Number(o.max_days);
    const total = Number(o.price_total);
    if (!Number.isFinite(min) || min < 1) continue;
    if (!Number.isFinite(max) || max < min) continue;
    if (!Number.isFinite(total) || total <= 0) continue;
    out.push({ min_days: min, max_days: max, price_total: total });
  }
  return out;
}

/**
 * Marca i tier (price_total × markup) per il lato cliente. Ritorna un nuovo
 * array; `mark` è la funzione markup della route (rate+rounding in closure).
 * I tier RAW (vendor €) restano per il payout vendor.
 */
export function markupMultidayTiers(raw: unknown, mark: (n: number) => number): MultidayTier[] {
  return parseMultidayTiers(raw).map((t) => ({
    min_days: t.min_days,
    max_days: t.max_days,
    price_total: Number(mark(t.price_total)) || 0,
  }));
}

/**
 * Trova il tier applicabile per `totalDays` (fasce non sovrapposte attese; se
 * si sovrappongono vince il range più STRETTO, poi il min_days più alto).
 * Ritorna null se nessun tier copre la durata.
 */
export function findMultidayTier(raw: unknown, totalDays: number): MultidayTier | null {
  const tiers = parseMultidayTiers(raw);
  const matching = tiers.filter((t) => totalDays >= t.min_days && totalDays <= t.max_days);
  if (matching.length === 0) return null;
  return matching.sort(
    (a, b) => (a.max_days - a.min_days) - (b.max_days - b.min_days) || b.min_days - a.min_days,
  )[0];
}
